<template>
  <div class="wifi-settings">
    <div class="flex-grid-row mb-2">
      <toggle
        id="wifi-toggle"
        :toggle.sync="device.network.wifiEnabled"
      >
      </toggle>
      <div class="ml-1">
        <strong>Wi-Fi {{ isWifiEnabled ? "Enabled" : "Disabled" }}</strong>
      </div>
      <div class="network-help network-help_floating">
        <a
          class="network-help__link"
          target="_blank"
          href="https://bioconnect.com/resources/install-guides/device-networking/"
        >
          Can't Connect to the Network?
        </a>
      </div>
    </div>

    <!-- Primary Network -->
    <div class="border p-1 wifi-div">
      <div
        class="wifi-header"
        @click="showHide('primary')"
      >
        <h4>Primary Network</h4>
      </div>
      <div
        v-show="wifiCardVisible == 'primary'"
        class="wifi-container"
      >
        <!-- WIFI Settings -->
        <div class="flex-grid">
          <template v-for="field in wifiSettings">
            <div
              :key="field.id"
              class="form-container column--two"
            >
              <template v-if="field.id === 'wifiPassword'">
                <label class="form-label">{{ field.label }}</label>
                <password-field
                  :id="field.id"
                  :validation="isValid(field.id)"
                  :password.sync="field.input"
                  :disabled="!isWifiEnabled"
                />
              </template>
              <template v-else>
                <label class="form-label">{{ field.label }}</label>
                <input
                  :id="field.id + '-input-device-settings-modal'"
                  v-model="field.input"
                  class="form-field"
                  :class="{ 'form-field--invalid': isValid(field.id) }"
                  type="text"
                  :disabled="!isWifiEnabled"
                />
              </template>
            </div>
          </template>
        </div>
        <div class="form-container mt-1">
          <label class="form-label">IP Settings</label>
          <div class="form-check">
            <label
              class="radio"
              for="dhcp-enabled"
            >
              <input
                id="dhcp-enabled"
                v-model="wifiDHCP"
                type="radio"
                :value="true"
                :disabled="!isWifiEnabled"
              />
              <span>Automatic IP Address (DHCP)</span>
            </label>
            <label
              class="radio ml-1"
              for="dhcp-disabled"
            >
              <input
                id="dhcp-disabled"
                v-model="wifiDHCP"
                type="radio"
                :value="false"
                :disabled="!isWifiEnabled"
              />
              <span>Static IP Address</span>
            </label>
          </div>
        </div>
        <!-- IP Settings -->
        <div class="flex-grid">
          <template v-for="field in ipSettings">
            <div
              :key="field.id"
              class="form-container column--two"
            >
              <label class="form-label">{{ field.label }}</label>
              <input
                :id="field.id + '-input-device-settings-modal'"
                v-model="field.input"
                class="form-field"
                :class="{ 'form-field--invalid': isValid(field.id) }"
                type="text"
                :disabled="wifiDHCP || !isWifiEnabled"
              />
            </div>
          </template>
        </div>
        <div class="form-container mt-1">
          <label class="form-label">DNS Settings</label>
          <div class="form-check">
            <label
              class="radio"
              for="dns-enabled"
            >
              <input
                id="dns-enabled"
                v-model="wifiStaticDNS"
                type="radio"
                :value="false"
                :disabled="!isWifiEnabled"
              />
              <span>Automatic DNS Server</span>
            </label>
            <label
              class="radio ml-1"
              for="dns-disabled"
            >
              <input
                id="dns-disabled"
                v-model="wifiStaticDNS"
                type="radio"
                :value="true"
                :disabled="!isWifiEnabled"
              />
              <span>Static DNS Server</span>
            </label>
          </div>
        </div>
        <!-- DNS Settings -->
        <div class="flex-grid">
          <template v-for="field in dnsSettings">
            <div
              :key="field.id"
              class="form-container column--two"
            >
              <label class="form-label">{{ field.label }}</label>
              <input
                :id="field.id + '-input-device-settings-modal'"
                v-model="field.input"
                class="form-field"
                :class="{ 'form-field--invalid': isValid(field.id) }"
                type="text"
                :disabled="!wifiStaticDNS || !isWifiEnabled"
              />
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- Backup Network -->
    <div class="border p-1 wifi-div mt-1">
      <div
        class="wifi-header"
        @click="showHide('backup')"
      >
        <h4>Alternate Network</h4>
      </div>
      <div
        v-show="wifiCardVisible == 'backup'"
        class="wifi-container"
      >
        <!-- <div class="wifi-container"> -->
        <!-- WIFI Settings -->
        <div class="flex-grid">
          <template v-for="field in backupWifiSettings">
            <div
              :key="field.id"
              class="form-container column--two"
            >
              <template v-if="field.id === 'backupWifiPassword'">
                <label class="form-label">{{ field.label }}</label>
                <password-field
                  :id="field.id"
                  :validation="isValid(field.id)"
                  :password.sync="field.input"
                  :disabled="!isWifiEnabled"
                />
              </template>
              <template v-else>
                <label class="form-label">{{ field.label }}</label>
                <input
                  :id="field.id + '-input-device-settings-modal'"
                  v-model="field.input"
                  class="form-field"
                  :class="{ 'form-field--invalid': isValid(field.id) }"
                  type="text"
                  :disabled="!isWifiEnabled"
                />
              </template>
            </div>
          </template>
        </div>
        <div class="form-container mt-1">
          <label class="form-label">IP Settings</label>
          <div class="form-check">
            <label
              class="radio"
              for="dhcp-backup-enabled"
            >
              <input
                id="dhcp-backup-enabled"
                v-model="backupWifiDhcp"
                type="radio"
                :value="true"
                :disabled="!isWifiEnabled"
              />
              <span>Automatic IP Address (DHCP)</span>
            </label>
            <label
              class="radio ml-1"
              for="dhcp-backup-disabled"
            >
              <input
                id="dhcp-backup-disabled"
                v-model="backupWifiDhcp"
                type="radio"
                :value="false"
                :disabled="!isWifiEnabled"
              />
              <span>Static IP Address</span>
            </label>
          </div>
        </div>
        <!-- IP Settings -->
        <div class="flex-grid">
          <template v-for="field in backupIpSettings">
            <div
              :key="field.id"
              class="form-container column--two"
            >
              <label class="form-label">{{ field.label }}</label>
              <input
                :id="field.id + '-input-device-settings-modal'"
                v-model="field.input"
                class="form-field"
                :class="{ 'form-field--invalid': isValid(field.id) }"
                type="text"
                :disabled="backupWifiDhcp || !isWifiEnabled"
              />
            </div>
          </template>
        </div>
        <div class="form-container mt-1">
          <label class="form-label">DNS Settings</label>
          <div class="form-check">
            <label
              class="radio"
              for="dns-backup-enabled"
            >
              <input
                id="dns-backup-enabled"
                v-model="backupWifiStaticDNS"
                type="radio"
                :value="false"
                :disabled="!isWifiEnabled"
              />
              <span>Automatic DNS Server</span>
            </label>
            <label
              class="radio ml-1"
              for="dns-backup-disabled"
            >
              <input
                id="dns-backup-disabled"
                v-model="backupWifiStaticDNS"
                type="radio"
                :value="true"
                :disabled="!isWifiEnabled"
              />
              <span>Static DNS Server</span>
            </label>
          </div>
        </div>
        <!-- DNS Settings -->
        <div class="flex-grid">
          <template v-for="field in backupDnsSettings">
            <div
              :key="field.id"
              class="form-container column--two"
            >
              <label class="form-label">{{ field.label }}</label>
              <input
                :id="field.id + '-input-device-settings-modal'"
                v-model="field.input"
                class="form-field"
                :class="{ 'form-field--invalid': isValid(field.id) }"
                type="text"
                :disabled="!backupWifiStaticDNS || !isWifiEnabled"
              />
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="alert alert-info mt-1">
      <strong>Having trouble connecting?</strong>
      <p>If you are unable to connect to your network, please follow these steps:</p>
      <ol>
        <li>
          On your smartphone navigate to Network Settings or equivalent and enable Hotspot "Network
          tethering" capability
        </li>
        <li>Change Phone hotspot name or SSID to "bioconnect" and Password to "bioconnect"</li>
        <li>
          While enabled for tethering, bring your phone in range of your device and wait up to 3 to
          5 minutes to see if it comes online
        </li>
      </ol>
      <div class="network-help">
        <p>Still can't connect?</p>
        <a
          class="network-help__link"
          target="_blank"
          href="https://bioconnect.com/resources/install-guides/device-networking/"
        >
          Network Troubleshooting
        </a>
      </div>
    </div>

    <div class="modal_footer">
      <button
        id="cancel-device-settings-modal"
        class="btn btn--grey"
        @click="emitCancel"
      >
        Cancel
      </button>
      <div class="error">
        {{ errorMessage }}
      </div>
      <button
        id="submit-device-settings-modal"
        class="btn"
        type="submit"
        @click="emitSettings"
      >
        Set Wi-Fi
      </button>
    </div>
  </div>
</template>

<script>
import PasswordField from '@/components/passwordField.vue'
import Toggle from '@/components/toggle.vue'
import REGEX from '@/global/utilities.js'

export default {
  name: 'WifiSettings',
  components: {
    passwordField: PasswordField,
    Toggle,
  },
  props: ['device'],
  data() {
    return {
      wifiCardVisible: '',
      errorMessage: '',
      invalidFields: [],
      wifiStaticDNS: false,
      backupWifiStaticDNS: false,
      wifiDHCP: true,
      backupWifiDhcp: false,
      wifiSettings: [
        {
          id: 'wifiSSID',
          label: 'Wi-Fi Name (SSID)',
          input: '',
          isRequired: true,
        },
        {
          id: 'wifiPassword',
          label: 'Wi-Fi Password',
          input: '',
          isRequired: true,
        },
      ],
      ipSettings: [
        {
          id: 'wifiIpAddressV4',
          label: 'IP Address',
          input: '',
          regex: REGEX.ipv4,
          isRequired: !this.wifiDHCP,
        },
        {
          id: 'wifiSubnet',
          label: 'Subnet',
          input: '',
          regex: REGEX.ipv4,
          isRequired: !this.wifiDHCP,
        },
        {
          id: 'wifiGateway',
          label: 'Gateway',
          input: '',
          regex: REGEX.ipv4,
          isRequired: !this.wifiDHCP,
        },
      ],
      dnsSettings: [
        {
          id: 'wifiPrimaryDNS',
          label: 'Primary DNS',
          input: '',
          regex: REGEX.ipv4,
          isRequired: this.wifiStaticDNS,
        },
        {
          id: 'wifiSecondaryDNS',
          label: 'Secondary DNS',
          input: '',
          regex: REGEX.ipv4,
          isRequired: this.wifiStaticDNS,
        },
      ],
      backupWifiSettings: [
        {
          id: 'backupWifiSSID',
          label: 'Wi-Fi Name (SSID)',
          input: '',
          isRequired: false,
        },
        {
          id: 'backupWifiPassword',
          label: 'Wi-Fi Password',
          input: '',
          isRequired: false,
        },
      ],
      backupIpSettings: [
        {
          id: 'backupWifiIpAddressV4',
          label: 'IP Address',
          input: '',
          regex: REGEX.ipv4,
          isRequired: !this.backupWifiDhcp,
        },
        {
          id: 'backupWifiSubnet',
          label: 'Subnet',
          input: '',
          regex: REGEX.ipv4,
          isRequired: !this.backupWifiDhcp,
        },
        {
          id: 'backupWifiGateway',
          label: 'Gateway',
          input: '',
          regex: REGEX.ipv4,
          isRequired: !this.backupWifiDhcp,
        },
      ],
      backupDnsSettings: [
        {
          id: 'backupWifiPrimaryDNS',
          label: 'Primary DNS',
          input: '',
          regex: REGEX.ipv4,
          isRequired: this.backupWifiStaticDNS,
        },
        {
          id: 'backupWifiSecondaryDNS',
          label: 'Secondary DNS',
          input: '',
          regex: REGEX.ipv4,
          isRequired: this.backupWifiStaticDNS,
        },
      ],
    }
  },
  computed: {
    isWifiEnabled() {
      return this.device.network.wifiEnabled
    },
  },
  watch: {
    wifiDHCP: {
      immediate: true,
      handler() {
        this.ipSettings.forEach((setting) => {
          setting.isRequired = !this.wifiDHCP
        })
      },
    },
    wifiStaticDNS: {
      immediate: true,
      handler() {
        this.dnsSettings.forEach((setting) => {
          setting.isRequired = this.wifiStaticDNS
        })
      },
    },
    backupWifiDhcp: {
      immediate: true,
      handler() {
        this.backupIpSettings.forEach((setting) => {
          setting.isRequired = !this.backupWifiDhcp
        })
      },
    },
    backupWifiStaticDNS: {
      immediate: true,
      handler() {
        this.backupDnsSettings.forEach((setting) => {
          setting.isRequired = this.backupWifiStaticDNS
        })
      },
    },
  },
  created() {
    if (this.device.network) {
      this.parseNetworkSettings()
    }
  },
  methods: {
    emitCancel() {
      this.$emit('cancel')
    },
    emitSettings() {
      this.errorMessage = ''
      this.checkInvalidFields()
      if (this.invalidFields.length > 0) {
        this.errorMessage = 'Please check if the settings you have entered are valid.'
      } else {
        const _device = this.combineSettings()
        this.$emit('submit-wifi', _device)
      }
    },
    isValid(fieldID) {
      const _isInvalid = this.invalidFields.filter((invalidField) => {
        return invalidField.id === fieldID
      })

      return _isInvalid.length > 0
    },
    // Package all new settings to be saved
    combineSettings() {
      const _settingsInput = {}
      const _networkFields = [
        ...this.wifiSettings,
        ...this.ipSettings,
        ...this.dnsSettings,
        ...this.backupWifiSettings,
        ...this.backupIpSettings,
        ...this.backupDnsSettings,
      ]
      _networkFields.forEach((field) => {
        _settingsInput[field.id] = field.input
      })
      const _newDeviceSettings = {
        ...this.device,
        network: {
          ...this.device.network,
          ..._settingsInput,
          wifiDhcp: this.wifiDHCP,
          wifiStaticDNS: this.wifiStaticDNS,
          backupWifiDhcp: this.backupWifiDhcp,
          backupWifiStaticDNS: this.backupWifiStaticDNS,
        },
      }
      return _newDeviceSettings
    },
    updateSettings(settings, condition, network) {
      if (condition) {
        return settings.map((setting) => {
          setting.input = network[setting.id]
          return setting
        })
      }
      return settings
    },
    parseNetworkSettings() {
      const {network} = this.device

      // Set defaults using logical OR
      this.wifiDHCP = network.wifiDhcp
      this.wifiStaticDNS = network.wifiStaticDNS
      this.backupWifiDhcp = network.backupWifiDhcp || true
      this.backupWifiStaticDNS = network.backupWifiStaticDNS || false

      // Map input fields based on network settings
      this.wifiSettings = this.updateSettings(this.wifiSettings, true, network)
      this.backupWifiSettings = this.updateSettings(this.backupWifiSettings, true, network)

      // Conditionally map IP and DNS settings based on DHCP and Static DNS settings
      this.ipSettings = this.updateSettings(this.ipSettings, !this.wifiDHCP, network)
      this.dnsSettings = this.updateSettings(this.dnsSettings, this.wifiStaticDNS, network)
      this.backupIpSettings = this.updateSettings(
        this.backupIpSettings,
        !this.backupWifiDhcp,
        network,
      )
      this.backupDnsSettings = this.updateSettings(
        this.backupDnsSettings,
        this.backupWifiStaticDNS,
        network,
      )
    },
    checkInvalidFields() {
      this.invalidFields = []
      const _fields = [
        ...this.wifiSettings,
        ...this.ipSettings,
        ...this.dnsSettings,
        ...this.backupWifiSettings,
        ...this.backupIpSettings,
        ...this.backupDnsSettings,
      ]
      const _requiredMissingInput = _fields.filter((field) => {
        return field.isRequired && !field.input
      })
      const _patternMismatch = _fields.filter((field) => {
        if (field.input && field.regex) {
          return !field.regex.test(field.input)
        }
      })
      this.invalidFields = [..._requiredMissingInput, ..._patternMismatch]
    },
    showHide(wifiCardName) {
      if (this.wifiCardVisible === wifiCardName) {
        this.wifiCardVisible = ''
      } else {
        this.wifiCardVisible = wifiCardName
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.wifi {
  &-div {
    cursor: pointer;
  }
  &-container {
    margin-top: 1em;
  }
  &-header {
    @include flex-display();
    @include flex-justify-content(space-between);
  }
}
.network-help {
  font-size: $small-font;
  &_floating {
    @include flex-display();
    @include flex-justify-content(flex-end);
    flex: 1;
  }
  &__link {
    text-decoration: underline;
    color: $dark-grey;
  }
}
</style>
